// Capturing an Audiotool project: the document, read and cut into a seed's
// worth of audio. The host-agnostic half of the round trip back into Seedbed.
//
// It is the pull path with a different destination — read the document once
// (read.ts), plan it once (shared/audiotool/seed-plan.ts, which is planPull
// underneath), then download each sample ONCE and cut each clip's own slice
// out of it verbatim (shared/audio/slice.ts). Live's pull writes those slices
// to disk and imports them; the browser hashes them and uploads them. Neither
// step is here, and neither needs to be.
//
// No Node, no Live, no DOM — the same rule arrangement-push.ts follows. What
// differs by host is injected: nothing yet but the client and the reporter,
// because everything below is bytes in and bytes out.
//
// A sample that fails to download costs only its own clips. The rest land, and
// what was lost is named in `missing` rather than failing the whole capture.
//
// With a render API (./render.ts), Audiotool's own renders replace what can't
// be rebuilt from samples — the instruments, the effects, the stretching:
//
//   • the MASTER is Audiotool's render of the project as captured, cut to the
//     arrangement plus its tail;
//   • each MIDI track gets its instrument's sound: the track rendered alone
//     (./track-renders.ts), cut into one bounce per clip;
//   • an ALTERED audio clip — stretched (Audiotool time-stretches, pitch kept),
//     faded, gained or pitch-shifted — takes its window of its track's render
//     instead of a verbatim cut, which would be the wrong length, tempo or
//     level.
//
// Unaltered audio clips stay verbatim cuts of their samples: Audiotool plays
// those sample-for-sample (checked, r = 1.000), they are dry, and identical
// cuts pool. Any render that fails falls back to exactly the old path.

import type { AudiotoolClient, SyncedDocument } from "@audiotool/nexus";

import { decodeAudio, type DecodedAudio } from "../../../shared/audio/master-mix.js";
import { cutSeconds, isAudible, tailEnd } from "../../../shared/audio/render-cut.js";
import { sliceFraction } from "../../../shared/audio/slice.js";
import { planSeedFromProjection, type CaptureClipAudio, type SeedPlan } from "../../../shared/audiotool/seed-plan.js";
import type { PullWarning } from "../../../shared/audiotool/pull-plan.js";
import type { Snapshot } from "../../../shared/seed-history.js";
import { errorText, isSdkError } from "./uploads.js";
import type { PushReporter } from "./progress.js";
import { readProjection } from "./read.js";
import type { RenderApi } from "./render.js";
import { pinIsolatedTracks, renderPins, type IsolationTarget, type Pins } from "./track-renders.js";

/** How long a master may ring on past the last beat — a reverb or delay tail. */
const MAX_TAIL_SEC = 10;

/** What a capture needs of the client — narrow, so tests can hand in a fake. */
export interface CaptureClient {
  open: (project: string) => Promise<Pick<SyncedDocument, "start" | "stop" | "createTransaction" | "queryEntities">>;
  samples: Pick<AudiotoolClient["samples"], "get" | "download">;
}

/** One clip's own audio, addressed as the uploads API addresses it. */
export interface CapturedClip {
  trackIndex: number;
  clipIndex: number;
  bytes: Uint8Array;
}

export interface Capture {
  snapshot: Snapshot;
  tempo: number;
  clips: CapturedClip[];
  /** Bounces of MIDI clips (their instrument's sound), addressed into `snapshot.midiTracks`. */
  midiClips: CapturedClip[];
  /** Audiotool's own render of the project, cut to the song; null without renders. */
  master: Uint8Array | null;
  warnings: PullWarning[];
  /** Samples that couldn't be fetched; their clips arrive silent. */
  missing: string[];
  /** What Audiotool rendered, what fell back, and why — for the producer's notes. */
  rendered: { master: boolean; tracks: number; failed: number; errors?: string[] } | null;
}

export async function captureProject(
  client: CaptureClient,
  project: { name: string; displayName: string },
  opts: { report?: PushReporter; signal?: AbortSignal; renders?: RenderApi } = {},
): Promise<Capture> {
  const say = opts.report ?? ((): void => {});

  say({ kind: "stage", stage: "reading" });
  const doc = await client.open(project.name);
  await doc.start();
  let plan: SeedPlan;
  let pins: Pins | null = null;
  let pinError: string | null = null;
  let targets: Map<string, { kind: "audio" | "midi"; index: number }> = new Map();
  try {
    const projection = await readProjection(doc, { projectId: project.name, displayName: project.displayName }, async (name) => {
      const meta = await client.samples.get(name);
      return isSdkError(meta) ? null : { displayName: meta.displayName, durationSeconds: meta.durationSeconds };
    });
    plan = planSeedFromProjection(projection);
    if (opts.renders) {
      const wanted = renderTargets(plan);
      targets = new Map(wanted.map((w) => [w.target.key, { kind: w.kind, index: w.index }]));
      say({ kind: "stage", stage: "rendering", done: 0, total: wanted.length + 1 });
      try {
        pins = await pinIsolatedTracks(doc, opts.renders, project.name, wanted.map((w) => w.target));
      } catch (e) {
        console.warn(`[Audiotool] couldn't pin renders; capturing from samples only: ${errorText(e)}`);
        pinError = errorText(e);
      }
    }
  } finally {
    // A capture holds no lock and keeps nothing open.
    await doc.stop();
  }

  // Audiotool renders while the samples download.
  let renderedDone = 0;
  const renders = pins && opts.renders
    ? renderPins(opts.renders, project.name, pins, {
        ...(opts.signal ? { signal: opts.signal } : {}),
        // Only as much of each render as the song and its tail can use.
        maxSeconds: songEndBeats(plan.snapshot) * (60 / (plan.tempo > 0 ? plan.tempo : 120)) + MAX_TAIL_SEC + 1,
        onRendered: () => say({ kind: "stage", stage: "rendering", done: ++renderedDone, total: pins!.tracks.size + 1 }),
      })
    : Promise.resolve(null);

  const sources = new Map<string, Uint8Array>();
  const missing: string[] = [];
  let n = 0;
  say({ kind: "stage", stage: "downloading", done: 0, total: plan.samples.length });
  for (const s of plan.samples) {
    if (opts.signal?.aborted) break;
    const label = s.displayName || s.name;
    say({ kind: "sample", id: s.name, displayName: label, state: "downloading" });
    try {
      // wav, not flac: the decoders on both hosts read WAV and AIFF only. The
      // SDK sometimes answers with AIFF under the .wav name — the slicer
      // dispatches on the magic bytes, so that costs nothing.
      const blob = await client.samples.download(s.name, { format: "wav" });
      if (isSdkError(blob)) throw blob;
      const bytes = new Uint8Array(await blob.arrayBuffer());
      sources.set(s.name, bytes);
      say({ kind: "sample", id: s.name, displayName: label, state: "downloaded", bytes: bytes.byteLength });
    } catch (e) {
      console.warn(`[Audiotool] couldn't download ${s.name}: ${errorText(e)}`);
      missing.push(label);
      say({ kind: "sample", id: s.name, displayName: label, state: "missing" });
    }
    say({ kind: "stage", stage: "downloading", done: ++n, total: plan.samples.length });
  }

  const rendered = await renders;
  const trackAudio = new Map<string, DecodedAudio>();
  for (const [key, wav] of rendered?.tracks ?? []) {
    const d = decodeAudio(wav);
    if (d) trackAudio.set(key, d);
  }
  const secondsPerBeat = 60 / (plan.tempo > 0 ? plan.tempo : 120);
  const trackCut = (key: string, startBeat: number, beats: number): Uint8Array | null => {
    const a = trackAudio.get(key);
    if (!a) return null;
    const from = startBeat * secondsPerBeat;
    const to = (startBeat + beats) * secondsPerBeat;
    return isAudible(a, from, to) ? cutSeconds(a, from, to) : null;
  };

  // One cut per distinct window; a loop's repetitions reuse theirs.
  say({ kind: "stage", stage: "cutting" });
  const cuts = new Map<string, Uint8Array | null>();
  const sampleCut = (want: CaptureClipAudio): Uint8Array | null => {
    const key = `${want.sampleName}|${want.fromFraction}|${want.toFraction}`;
    if (!cuts.has(key)) {
      const src = sources.get(want.sampleName);
      // Always through the slicer, even for a whole sample: it rewrites the
      // container as `fmt ` + `data` only (both verbatim), dropping the LIST
      // chunk Audiotool adds on upload — so audio that went out from Seedbed
      // comes back byte-identical (and pools), not merely PCM-identical.
      cuts.set(key, !src ? null : (sliceFraction(src, want.fromFraction, want.toFraction) ?? (want.fromFraction === 0 && want.toFraction === 1 ? src : null)));
    }
    return cuts.get(key) ?? null;
  };
  /**
   * A clip's audio: its window of `renderKey`'s render when Audiotool alters
   * it (stretched, faded, gained, pitch-shifted, processed) — the only
   * faithful source — else its sample, verbatim.
   */
  const audioFor = (want: CaptureClipAudio, renderKey: string, span: { startTime: number; duration: number }): Uint8Array | null =>
    (want.altered ? trackCut(renderKey, span.startTime, span.duration) : null) ?? sampleCut(want);

  const clips: CapturedClip[] = [];
  for (const want of plan.clipAudio) {
    if (opts.signal?.aborted) break;
    const info = plan.snapshot.audioTracks[want.trackIndex]!.arrangementClips[want.clipIndex]!;
    // A clip whose sample failed, or whose window came out empty, still exists
    // in the snapshot — it simply has no audio, which the seed already models.
    const bytes = audioFor(want, `a${want.trackIndex}`, info);
    if (bytes) clips.push({ trackIndex: want.trackIndex, clipIndex: want.clipIndex, bytes });
  }

  // MIDI clips' sound: from the track's sound lane when it came out with one
  // (the seed's own bounce, verbatim), else the instrument, from the track's
  // own render.
  const midiClips: CapturedClip[] = [];
  for (const want of plan.midiBounces) {
    const info = plan.snapshot.midiTracks![want.trackIndex]!.arrangementClips[want.clipIndex]!;
    const bytes = audioFor(want, `m${want.trackIndex}`, info);
    if (bytes) midiClips.push({ trackIndex: want.trackIndex, clipIndex: want.clipIndex, bytes });
  }
  (plan.snapshot.midiTracks ?? []).forEach((t, trackIndex) => {
    if (plan.sources.midi[trackIndex]?.paired) return;
    t.arrangementClips.forEach((c, clipIndex) => {
      const bytes = trackCut(`m${trackIndex}`, c.startTime, c.duration);
      if (bytes) midiClips.push({ trackIndex, clipIndex, bytes });
    });
  });

  // The master: the project as Audiotool plays it, the song plus its tail.
  let master: Uint8Array | null = null;
  if (rendered?.full) {
    const full = decodeAudio(rendered.full);
    if (full) {
      const end = songEndBeats(plan.snapshot) * secondsPerBeat;
      master = cutSeconds(full, 0, tailEnd(full, end, MAX_TAIL_SEC));
    }
  }

  // The stretch warning only stands for clips no render replaced.
  const stillStretched =
    plan.clipAudio.some((w) => w.stretched && !trackAudio.has(`a${w.trackIndex}`)) ||
    plan.midiBounces.some((w) => w.stretched && !trackAudio.has(`m${w.trackIndex}`));
  const warnings = (stillStretched ? plan.warnings : plan.warnings.filter((w) => w.code !== "stretched")).map((w) =>
    // Rendered, the devices' SOUND did come across — only the devices didn't.
    w.code === "device-dropped" && (master !== null || midiClips.length > 0)
      ? { ...w, detail: w.detail.replace(/ MIDI tracks arrive without an instrument\.$/, " Their sound came across as audio, rendered by Audiotool: in the master and as each MIDI clip's bounce.") }
      : w,
  );

  return {
    snapshot: plan.snapshot,
    tempo: plan.tempo,
    clips,
    midiClips,
    master,
    warnings,
    missing,
    rendered: pins
      ? {
          master: master !== null,
          tracks: trackAudio.size,
          failed: targets.size - trackAudio.size,
          ...(rendered?.errors.length || pinError ? { errors: [...(pinError ? [pinError] : []), ...(rendered?.errors ?? [])] } : {}),
        }
      : pinError
        ? { master: false, tracks: 0, failed: targets.size, errors: [pinError] }
        : null,
  };
}

/**
 * Which tracks need a render of their own: every MIDI track with clips (for
 * its instrument's sound) and every audio track with a clip Audiotool alters
 * (stretch, gain, fade, pitch). A track
 * cabled to no mixer channel plays silent in Audiotool; nothing to render.
 */
function renderTargets(plan: SeedPlan): Array<{ kind: "audio" | "midi"; index: number; target: IsolationTarget }> {
  const out: Array<{ kind: "audio" | "midi"; index: number; target: IsolationTarget }> = [];
  (plan.snapshot.midiTracks ?? []).forEach((t, index) => {
    const src = plan.sources.midi[index];
    // A paired track's sound is its sound lane's samples — rendered only
    // where Audiotool alters them.
    const wanted = src?.paired ? plan.midiBounces.some((b) => b.trackIndex === index && b.altered) : t.arrangementClips.length > 0;
    if (wanted && src?.stripId) {
      out.push({ kind: "midi", index, target: { key: `m${index}`, trackId: src.trackId, stripId: src.stripId } });
    }
  });
  plan.snapshot.audioTracks.forEach((_, index) => {
    const src = plan.sources.audio[index];
    if (src?.stripId && plan.clipAudio.some((w) => w.trackIndex === index && w.altered)) {
      out.push({ kind: "audio", index, target: { key: `a${index}`, trackId: src.trackId, stripId: src.stripId } });
    }
  });
  return out;
}

/** The last beat anything plays. */
function songEndBeats(s: Snapshot): number {
  let end = 0;
  for (const t of s.audioTracks) for (const c of t.arrangementClips) end = Math.max(end, c.startTime + c.duration);
  for (const t of s.midiTracks ?? []) for (const c of t.arrangementClips) end = Math.max(end, c.startTime + c.duration);
  return end;
}
